import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildApiUrl, buildAssetUrl } from '../../../config/apiConfig';
import ProductCard from './ProductCard';
import './TrainingCenterSection.css';
import { logError } from '../../../utils/logger';

const MAX_COURSES = 6;

const normalizeCourses = (products) =>
  (products || [])
    .filter((p) => p.status === 'active')
    .map((course) => {
      /* La ficha pública del curso usa /api/courses/public/:id — el id de ruta es el curso (referenceId), no el id del producto. */
      const routeId =
        course.referenceId ??
        course.reference_id ??
        course.course?.id ??
        course.id ??
        course.productId;
      const productKey = course.id ?? course.productId ?? routeId;

      return {
        id: productKey,
        type: 'course',
        name: course.name || course.nombre || course.course?.nombre || course.course?.titulo || 'Curso',
        description:
          course.description ||
          course.descripcionBreve ||
          course.course?.descripcionBreve ||
          'Descripción no disponible',
        originalPrice: parseFloat(course.price || course.precio || 0),
        finalPrice:
          parseFloat(course.price || course.precio || 0) *
          (1 - parseFloat(course.discount || course.discountPercent || 0) / 100),
        discountPercent: parseFloat(course.discount || course.discountPercent || 0),
        imageUrl: course.image
          ? buildAssetUrl(course.image)
          : course.imagen
            ? buildAssetUrl(course.imagen)
            : course.course?.imagen
              ? buildAssetUrl(course.course.imagen)
              : '',
        placeholderIcon: 'fas fa-graduation-cap',
        detailUrl: routeId != null && routeId !== '' ? `/courses/${routeId}` : null,
      };
    })
    .filter(Boolean);

const TrainingCenterSection = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/products/public/courses'));
        if (!response.ok) throw new Error('Error al cargar cursos');
        const data = await response.json();
        setCourses(normalizeCourses(data.products));
      } catch (err) {
        logError('[TrainingCenterSection] Error cargando cursos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleCourseClick = useCallback(
    (course) => {
      if (course?.detailUrl) navigate(course.detailUrl);
    },
    [navigate],
  );

  const visible = courses.slice(0, MAX_COURSES);

  return (
    <section
      className="training-center section section--lg"
      aria-labelledby="training-center-heading"
    >
      <div className="container">
        <header className="section-header training-center__header">
          <h2 id="training-center-heading" className="section-title training-center__title">
            <span className="training-center__title-line">Centro de Formación</span>
            <span className="training-center__title-brand">
              
              <span className="training-center__title-accent">Always Aligners</span>
              
            </span>
          </h2>
          <p className="training-center__intro">
            Formación clínica en alineadores para profesionales que buscan integrar diagnóstico,
            biomecánica y planificación digital en su práctica diaria.
          </p>
        </header>

        {loading && (
          <div className="training-center__state training-center__state--loading" role="status">
            Cargando cursos…
          </div>
        )}

        {!loading && visible.length === 0 && (
          <p className="training-center__state training-center__state--empty">
            Pronto sumaremos cursos a esta vitrina. Mientras tanto podés explorar el catálogo
            completo.
          </p>
        )}

        {!loading && visible.length > 0 && (
          <div className="training-center__grid">
            {visible.map((course) => (
              <div key={course.id} className="training-center__card-wrap">
                <ProductCard
                  item={course}
                  onCardClick={handleCourseClick}
                  onPrimaryAction={handleCourseClick}
                  primaryActionLabel="Ver curso"
                  primaryActionIcon="fas fa-arrow-right"
                  isCardClickable
                />
              </div>
            ))}
          </div>
        )}

        {!loading && (
          <div className="training-center__footer">
            <Link to="/courses" className="btn btn--secondary training-center__cta">
              Ver todos los cursos
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default TrainingCenterSection;
